import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, X } from 'lucide-react';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import type { AiSettingsResponse } from '../../types/ai';
import { AiPromptLibrary } from './AiPromptLibrary';
import { AiSkills } from './AiSkills';

type Tab = 'chat' | 'skills' | 'prompts';

const TABS: readonly { id: Tab; label: string }[] = [
  { id: 'chat', label: 'Chat' },
  { id: 'skills', label: 'Skills' },
  { id: 'prompts', label: 'Prompt library' },
];

/** The API accepts at most 8 messages of 2,000 characters each. */
const MAX_HISTORY = 8;
const MAX_CONTENT = 2000;

/** The scrolling body of the panel. The stable gutter keeps the scrollbar off the content. */
const BODY_CLASS =
  'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3 [scrollbar-gutter:stable] [scrollbar-width:thin]';

interface ChatLine {
  role: 'user' | 'assistant';
  content: string;
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'The assistant could not answer. Try again.';
}

function Chat({ prefill, onPrefillUsed }: { prefill: string; onPrefillUsed: () => void }) {
  const [lines, setLines] = useState<ChatLine[]>([]);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  // A prompt picked in the library lands in the box once, with its first
  // [bracket] selected so typing replaces it.
  useEffect(() => {
    if (prefill === '') return;
    setDraft(prefill);
    onPrefillUsed();
    setTimeout(() => {
      const start = prefill.indexOf('[');
      const end = prefill.indexOf(']', start);
      inputRef.current?.focus();
      if (start >= 0 && end > start) inputRef.current?.setSelectionRange(start, end + 1);
    }, 0);
  }, [prefill, onPrefillUsed]);

  const send = useMutation({
    mutationFn: (history: ChatLine[]) =>
      apiFetch<{ reply: string }>('/ai/chat', {
        method: 'POST',
        body: {
          messages: history
            .slice(-MAX_HISTORY)
            .map((l) => ({ role: l.role, content: l.content.slice(0, MAX_CONTENT) })),
        },
      }),
    onSuccess: (res) => {
      setLines((prev) => [...prev, { role: 'assistant', content: res.reply }]);
      void queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
    },
  });

  // Keep the newest message in view.
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [lines.length, send.isPending]);

  function submit() {
    const text = draft.trim();
    if (text === '' || send.isPending) return;
    const next: ChatLine[] = [...lines, { role: 'user', content: text.slice(0, MAX_CONTENT) }];
    setLines(next);
    setDraft('');
    send.mutate(next);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={logRef}
        role="log"
        aria-label="Conversation"
        aria-live="polite"
        className={cn(BODY_CLASS, 'flex flex-col gap-2')}
      >
        {lines.length === 0 ? (
          <p className="m-auto max-w-[300px] py-6 text-center text-sm text-muted">
            Ask how something works in the app, or ask for a draft. Do not paste contact names,
            emails or order details.
          </p>
        ) : null}
        {lines.map((l, i) => (
          <div
            key={i}
            className={cn(
              'max-w-[85%] whitespace-pre-wrap break-words rounded-[10px] px-3 py-2 text-sm',
              l.role === 'user' ? 'self-end bg-accent-soft' : 'self-start bg-bg',
            )}
          >
            <span className="sr-only">{l.role === 'user' ? 'You: ' : 'Assistant: '}</span>
            {l.content}
          </div>
        ))}
        {send.isPending ? (
          <div role="status" className="self-start px-1 text-sm text-muted">
            Thinking…
          </div>
        ) : null}
      </div>
      <div className="flex flex-col gap-2 border-t border-border px-4 pb-3 pt-3">
        {send.isError ? <Alert className="px-3 py-2">{errorText(send.error)}</Alert> : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ai-chat-input">Your message</Label>
          <textarea
            id="ai-chat-input"
            ref={inputRef}
            rows={2}
            className="max-h-[160px] min-h-[64px] w-full resize-y rounded-[9px] border border-zinc-300 px-3 py-2 text-sm"
            value={draft}
            maxLength={MAX_CONTENT}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <div className="flex items-start justify-between gap-3">
          <p className="m-0 text-xs text-muted">
            The assistant only writes text and drafts. It never sends or changes anything.
          </p>
          <Button
            size="sm"
            className="shrink-0"
            disabled={draft.trim() === '' || send.isPending}
            onClick={submit}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Underline tabs with arrow-key, Home and End support (WAI-ARIA tabs pattern). */
function Tabs({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }) {
  const refs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const i = TABS.findIndex((t) => t.id === tab);
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const id = TABS[next]!.id;
    onChange(id);
    refs.current[id]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label="Assistant"
      onKeyDown={onKeyDown}
      className="flex gap-4 border-b border-border px-4"
    >
      {TABS.map((t) => (
        <button
          key={t.id}
          ref={(el) => {
            refs.current[t.id] = el;
          }}
          type="button"
          role="tab"
          id={`ai-tab-${t.id}`}
          aria-selected={tab === t.id}
          aria-controls={`ai-tabpanel-${t.id}`}
          tabIndex={tab === t.id ? 0 : -1}
          onClick={() => onChange(t.id)}
          className={cn(
            '-mb-px border-b-2 py-2.5 text-sm transition-colors focus-visible:outline-offset-[-2px]',
            tab === t.id
              ? 'border-accent font-medium text-text'
              : 'border-transparent text-muted hover:text-text',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The assistant in the bottom right corner of the app (K7), like a chat widget
 * on a website. Editor and admin only. The button opens a panel with the chat,
 * the skills and the prompt library. Off by default: until an admin switches
 * it on, the panel only says so.
 */
export function AiAssistant() {
  const { isEditor, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('chat');
  const [prefill, setPrefill] = useState('');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => apiFetch<AiSettingsResponse>('/ai/settings'),
    enabled: isEditor,
    staleTime: 60_000,
  });

  const clearPrefill = useCallback(() => setPrefill(''), []);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  function close() {
    setOpen(false);
    launcherRef.current?.focus();
  }

  if (!isEditor) return null;

  const usable = data?.enabled === true && data.available;

  return (
    <section aria-label="AI assistant">
      {/* Keeps the end of a page clear of the corner button. */}
      <div aria-hidden="true" className="h-8" />
      <button
        ref={launcherRef}
        type="button"
        aria-label="AI assistant"
        aria-expanded={open}
        aria-controls="ai-panel"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-4 right-4 z-30 grid h-[52px] w-[52px] place-items-center rounded-full bg-accent text-white shadow-lg transition-transform duration-150 hover:scale-105 md:bottom-6 md:right-6"
      >
        {open ? <X size={22} aria-hidden="true" /> : <Sparkles size={22} aria-hidden="true" />}
      </button>
      <div
        id="ai-panel"
        ref={panelRef}
        tabIndex={-1}
        hidden={!open}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close();
        }}
        className={cn(
          // Phone: a full-width bottom sheet over the corner button (the header has a close button).
          'fixed inset-x-0 bottom-0 z-40 h-[min(640px,calc(100dvh-48px))] flex-col overflow-hidden rounded-t-[14px] border border-b-0 border-border bg-card pb-[env(safe-area-inset-bottom)] shadow-lg outline-none',
          // Larger screens: a floating panel above the corner button.
          'sm:inset-x-auto sm:bottom-[80px] sm:right-4 sm:z-30 sm:h-[min(640px,calc(100dvh-104px))] sm:w-[min(440px,calc(100vw-32px))] sm:rounded-[14px] sm:border-b sm:pb-0 md:bottom-[92px] md:right-6',
          open ? 'flex' : 'hidden',
        )}
      >
        <div className="flex shrink-0 items-center gap-2 py-2 pl-4 pr-2">
          <Sparkles size={16} aria-hidden="true" className="text-accent" />
          <h2 className="m-0 text-sm font-semibold">AI assistant</h2>
          <button
            type="button"
            aria-label="Close AI assistant"
            onClick={close}
            className="ml-auto grid h-8 w-8 place-items-center rounded-[7px] text-muted hover:bg-border-soft hover:text-text"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        {isLoading ? (
          <div className={cn(BODY_CLASS, 'border-t border-border')}>
            <p role="status" className="m-0 text-sm text-muted">
              Loading…
            </p>
          </div>
        ) : !usable ? (
          <div className={cn(BODY_CLASS, 'flex flex-col gap-4 border-t border-border')}>
            <div className="flex flex-col gap-1 text-sm">
              {isError ? (
                <Alert className="px-3 py-2">Could not load the assistant. Try again later.</Alert>
              ) : (
                <p className="m-0">
                  {data && !data.available
                    ? 'The AI assistant is not available in this environment.'
                    : 'The AI assistant is switched off.'}
                </p>
              )}
              {isAdmin ? (
                <Link to="/indstillinger/ai" className="self-start text-link">
                  Open AI assistant settings
                </Link>
              ) : (
                <p className="m-0 text-muted">An admin can turn it on under Settings.</p>
              )}
            </div>
            {/* The prompt library is only saved text, so it works while the assistant is off. */}
            <AiPromptLibrary onUse={() => undefined} canUse={false} />
          </div>
        ) : (
          <>
            <Tabs tab={tab} onChange={setTab} />
            {/* Stays mounted so the conversation survives a look at the other tabs. */}
            <div
              role="tabpanel"
              id="ai-tabpanel-chat"
              aria-labelledby="ai-tab-chat"
              hidden={tab !== 'chat'}
              className={cn('min-h-0 flex-1 flex-col', tab === 'chat' ? 'flex' : 'hidden')}
            >
              <Chat prefill={prefill} onPrefillUsed={clearPrefill} />
            </div>
            {tab === 'skills' ? (
              <div
                role="tabpanel"
                id="ai-tabpanel-skills"
                aria-labelledby="ai-tab-skills"
                className={BODY_CLASS}
              >
                <AiSkills />
              </div>
            ) : null}
            {tab === 'prompts' ? (
              <div
                role="tabpanel"
                id="ai-tabpanel-prompts"
                aria-labelledby="ai-tab-prompts"
                className={BODY_CLASS}
              >
                <AiPromptLibrary
                  canUse
                  onUse={(text) => {
                    setPrefill(text);
                    setTab('chat');
                  }}
                />
              </div>
            ) : null}
            <p className="m-0 shrink-0 border-t border-border bg-hover px-4 py-2 text-xs text-muted">
              Today: {data?.usage.requests ?? 0} of {data?.dailyRequestCap ?? 0} requests used.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
